/**
 * 카테고리/메뉴 스타일 상수 — DB 의존 제거, 프론트 일원화 관리
 * - i18n(label/description) 은 DB(`business.category_translations`) 유지
 * - 모든 시각 스타일/애니메이션 클래스는 본 파일에서만 정의
 *   → MainMenu 등 컴포넌트는 본 상수만 import 하여 사용
 *   → Tailwind JIT 가 정적 리터럴을 정상 감지
 *
 * 새 카테고리/스타일 추가 시 본 파일만 수정 (어드민 입력 X)
 */

import type { LucideIcon } from "lucide-react";
import {
  Martini,
  Sparkles,
  Dices,
  ArrowLeftRight,
  BedDouble,
  UtensilsCrossed,
  Plane,
  Building2,
  Users,
  Briefcase,
  ShoppingBag,
  Coins,
  Map,
  Flag,
  MessagesSquare,
  Spade,
  Car,
  HelpCircle,
} from "lucide-react";

// ------------------------------------------------------------------
// 카테고리 그라디언트 (key → Tailwind from-* to-*)
// ------------------------------------------------------------------
export const CATEGORY_GRADIENTS: Record<string, string> = {
  nightlife:  "from-fuchsia-500 to-pink-500",
  massage:    "from-cyan-500 to-sky-500",
  casino:     "from-amber-500 to-yellow-500",
  exchange:   "from-green-500 to-emerald-500",
  stay:       "from-indigo-500 to-blue-500",
  food:       "from-orange-500 to-red-500",
  travel:     "from-sky-500 to-indigo-500",
  realestate: "from-violet-500 to-purple-500",
  agency:     "from-slate-500 to-zinc-500",
  jobs:       "from-blue-600 to-cyan-600",
  market:     "from-teal-500 to-green-500",
  crypto:     "from-yellow-500 to-amber-600",
  travelinfo: "from-rose-500 to-pink-500",
  golf:       "from-lime-500 to-green-600",
  dating:     "from-pink-500 to-rose-500",
  holdem:     "from-red-600 to-rose-600",
};

export const DEFAULT_CATEGORY_GRADIENT = "from-slate-500 to-zinc-500";

export function getCategoryGradient(key: string | null | undefined): string {
  if (!key) return DEFAULT_CATEGORY_GRADIENT;
  return CATEGORY_GRADIENTS[key] || DEFAULT_CATEGORY_GRADIENT;
}

// ------------------------------------------------------------------
// 카테고리 아이콘 (요구사항 #254 #7: 카테고리별 시각적 식별자)
// - 임시 lucide-react 아이콘 매핑 (디자이너 3D 아이콘 자산 입수 시 SVG 로 교체)
// - 사용처: MainMenu.tsx (가로 버튼 / 모바일 드롭다운)
// ------------------------------------------------------------------
export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  nightlife: Martini, // 유흥업소
  massage: Sparkles, // 마사지
  casino: Dices, // 카지노
  exchange: ArrowLeftRight, // 환전소
  stay: BedDouble, // 숙소
  food: UtensilsCrossed, // 음식점
  travel: Plane, // 여행사
  realestate: Building2, // 부동산
  agency: Users, // 대행/뷰티서비스
  jobs: Briefcase, // 구인구직
  market: ShoppingBag, // 사고팔고
  crypto: Coins, // 코인
  travelinfo: Map, // 여행정보
  golf: Flag, // 골프
  dating: MessagesSquare, // 채팅/만남
  holdem: Spade, // 홀덤 (파일명)
  // #410 — DB key 직접 매핑 (PNG 자산 없어 lucide 로 표시. 디자이너 PNG 받으면 CATEGORY_ICON_FILES 에 추가)
  Holdem: Spade, // 홀덤
  Vehicles: Car, // 차량
};

export function getCategoryIcon(key: string | null | undefined): LucideIcon {
  if (!key) return HelpCircle;
  return CATEGORY_ICONS[key] || HelpCircle;
}

// ------------------------------------------------------------------
// 카테고리 3D PNG 이미지 (요구사항 #254 #7 — image 3 에서 추출한 디자이너 자산)
// public/category-icons/*.png 와 매핑
// DB business.categories.key 의 영문 라벨(공백·대소문자 보존) → 파일명
// 매핑되지 않은 키는 lucide 아이콘으로 fallback
// ------------------------------------------------------------------
export const CATEGORY_ICON_FILES: Record<string, string> = {
  Nightlife: "nightlife",
  Massage: "massage",
  Casino: "casino",
  "Money exchange": "exchange",
  Accommodation: "stay",
  Restaurant: "food",
  "Travel agency&Golf": "travel",
  "Real estate ": "realestate", // DB 키 trailing space 포함
  "Real estate": "realestate", // trim 된 변종
  "Beauty services": "agency",
  Other: "etc",
  Market: "market",
  "Jobs ": "jobs", // DB 키 trailing space 포함
  Jobs: "jobs",
  Crypto: "crypto",
  "International Exchange": "travelinfo",
  // #410 — DB icon_url 미설정 카테고리. 로컬 3D PNG fallback (Icons8 3D Fluency 동일 세트)
  Holdem: "holdem", // 홀덤 — 3D 트럼프 카드
  Vehicles: "vehicles", // 차량 — 3D 자동차
};

export function getCategoryIconImageSrc(
  key: string | null | undefined,
): string | null {
  if (!key) return null;
  const name = CATEGORY_ICON_FILES[key];
  return name ? `/category-icons/${name}.png` : null;
}

// ------------------------------------------------------------------
// 카테고리 바 (MainMenu 컨테이너)
// ------------------------------------------------------------------
export const CATEGORY_BAR = {
  // 외곽 컨테이너 (라이트 테마, 상하 1px gray 구분선)
  outer:    "border-t border-b border-slate-300 bg-white py-0.5 md:py-2",
  // 내부 wrapper — 검색바와 동일한 좌우 정렬 (요구사항 #248: 화면 양쪽 가득)
  inner:    "flex mx-auto w-full max-w-[var(--content-width)] items-center gap-2 px-[3.75px] sm:px-[7.5px]",
  // 데스크탑 가로 스크롤 행 (스크롤바 숨김)
  scrollX:  "hidden md:flex items-center gap-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
} as const;

// ------------------------------------------------------------------
// 카테고리 버튼 (글로우 애니메이션 + 활성/비활성)
// ------------------------------------------------------------------
export const CATEGORY_BUTTON = {
  // 공통 — 사이즈/여백/간격/한 줄
  base:     "!px-3 !py-px !text-xs whitespace-nowrap",
  // 글로우 애니메이션 클래스 (index.css 정의)
  glow:     "glow-white",
  // 활성 상태 — 빨강 굵게 + sky-200 배경 (hover bg 와 동일색 → hover 시에도 배경 유지)
  active:   "text-red-600 font-bold !bg-[#bae6fd]",
  // 비활성 상태 — black (요구사항 #311)
  inactive: "text-slate-900",
} as const;

/** 활성/비활성 상태에 따른 className 조합 헬퍼 */
export function categoryButtonClass(active: boolean): string {
  return [
    CATEGORY_BUTTON.glow,
    CATEGORY_BUTTON.base,
    active ? CATEGORY_BUTTON.active : CATEGORY_BUTTON.inactive,
  ].join(" ");
}

// ------------------------------------------------------------------
// 카테고리 hover 툴팁 (description 표시)
// ------------------------------------------------------------------
export const CATEGORY_TOOLTIP = {
  // 버튼 wrapper (단순 shrink-0, hover 상태는 React state 로 관리)
  wrapper: "shrink-0",
  // Portal 로 body 에 fixed 렌더되는 툴팁 박스
  // (carousel/overflow-x-auto 컨테이너 안 absolute 툴팁이 잘리는 문제 회피)
  box: "pointer-events-none fixed z-[9999] -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2 py-1 text-[11px] text-white shadow-lg transition-opacity duration-150",
} as const;
